import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { createHttpClient } from '@shared/api/httpClient';
import type {
  AccountMembership,
  AccountMembershipsResponse,
  ClientRecord,
  ClientsResponse,
  ClientUserRecord,
  ClientUsersResponse,
  ManagedAwsAccount,
  ManagedAwsAccountResponse,
  ManagedAwsAccountsResponse,
  RunClientsResponse,
} from '@shared/types/api';
import { formatLocalDateTime } from '@shared/utils/dateTime';
import { statusPillClassName } from '@/utils/statusPill';

type AccountFormState = {
  accountId: string;
  displayName: string;
  region: string;
  cmmcLevel: string;
  managementAccountId: string;
  logArchiveAccountId: string;
  externalId: string;
  initialAdminUsername: string;
  accountStatus: 'ACTIVE' | 'DISABLED';
  clientEvidenceWorkflowEnabled: boolean;
};

type MembershipFormState = {
  username: string;
  accountRole: 'ACCOUNT_ADMIN' | 'ACCOUNT_READONLY';
};

const EMPTY_ACCOUNT_FORM: AccountFormState = {
  accountId: '',
  displayName: '',
  region: 'us-east-1',
  cmmcLevel: 'L2',
  managementAccountId: '',
  logArchiveAccountId: '',
  externalId: '',
  initialAdminUsername: '',
  accountStatus: 'DISABLED',
  clientEvidenceWorkflowEnabled: false,
};

const EMPTY_MEMBERSHIP_FORM: MembershipFormState = {
  username: '',
  accountRole: 'ACCOUNT_READONLY',
};

function accountLabel(account: ManagedAwsAccount): string {
  return account.displayName || `AWS Account ${account.accountId}`;
}

function memberLabel(member: AccountMembership): string {
  return member.email || member.username || member.userSub;
}

function defaultAccountAdministrator(client: ClientRecord | null, users: ClientUserRecord[]): ClientUserRecord | null {
  const eligibleAdministrators = users.filter((user) => (
    user.enabled !== false && String(user.groupName || '').toUpperCase() === 'CLIENT_ADMIN'
  ));
  if (eligibleAdministrators.length === 0) return null;

  const preferredValues = [
    String(client?.cognitoUsername || '').trim().toLowerCase(),
    String(client?.clientEmail || '').trim().toLowerCase(),
    String(client?.contactEmail || '').trim().toLowerCase(),
    ...((client?.clientAdminEmails ?? []).map((email) => String(email || '').trim().toLowerCase())),
  ].filter(Boolean);

  return eligibleAdministrators.find((user) => {
    const candidates = [user.username, user.email].map((value) => String(value || '').trim().toLowerCase());
    return preferredValues.some((preferred) => candidates.includes(preferred));
  }) ?? eligibleAdministrators[0];
}

function newAccountForm(initialAdminUsername = ''): AccountFormState {
  return { ...EMPTY_ACCOUNT_FORM, initialAdminUsername };
}

function editAccountFormFromAccount(account: ManagedAwsAccount): AccountFormState {
  return {
    accountId: account.accountId,
    displayName: account.displayName || '',
    region: account.region || 'us-east-1',
    cmmcLevel: account.cmmcLevel || 'L2',
    managementAccountId: account.managementAccountId || '',
    logArchiveAccountId: account.logArchiveAccountId || '',
    externalId: '',
    initialAdminUsername: '',
    accountStatus: String(account.accountStatus || '').toUpperCase() === 'ACTIVE' ? 'ACTIVE' : 'DISABLED',
    clientEvidenceWorkflowEnabled: account.clientEvidenceWorkflowEnabled === true,
  };
}


export default function ClientAccountsPage() {
  const { clientId: routeClientId } = useParams();
  const clientId = String(routeClientId || '').trim();
  const api = useMemo(() => createHttpClient({ portal: 'admin' }), []);
  const [client, setClient] = useState<ClientRecord | null>(null);
  const [accounts, setAccounts] = useState<ManagedAwsAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [members, setMembers] = useState<AccountMembership[]>([]);
  const [clientUsers, setClientUsers] = useState<ClientUserRecord[]>([]);
  const [accountForm, setAccountForm] = useState<AccountFormState>(EMPTY_ACCOUNT_FORM);
  const [editAccountForm, setEditAccountForm] = useState<AccountFormState>(EMPTY_ACCOUNT_FORM);
  const [membershipForm, setMembershipForm] = useState<MembershipFormState>(EMPTY_MEMBERSHIP_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [runningAction, setRunningAction] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [accountFilter, setAccountFilter] = useState('');

  const selectedAccount = accounts.find((account) => account.accountId === selectedAccountId) ?? null;
  const eligibleAccountAdministrators = useMemo(
    () => clientUsers.filter((user) => user.enabled !== false && String(user.groupName || '').toUpperCase() === 'CLIENT_ADMIN'),
    [clientUsers],
  );
  const defaultAdministrator = useMemo(
    () => defaultAccountAdministrator(client, clientUsers),
    [client, clientUsers],
  );
  const filteredAccounts = useMemo(() => {
    const query = accountFilter.trim().toLowerCase();
    if (!query) return accounts;
    return accounts.filter((account) => (
      accountLabel(account).toLowerCase().includes(query)
      || account.accountId.includes(query)
      || String(account.managementAccountId || '').includes(query)
      || String(account.logArchiveAccountId || '').includes(query)
      || String(account.region || '').toLowerCase().includes(query)
      || String(account.accountStatus || '').toLowerCase().includes(query)
    ));
  }, [accountFilter, accounts]);

  const loadAccounts = useCallback(async () => {
    if (!clientId) return;
    const response = await api.get<ManagedAwsAccountsResponse>(`/admin/clients/${encodeURIComponent(clientId)}/accounts`);
    const nextAccounts = response.accounts ?? [];
    setAccounts(nextAccounts);
    setSelectedAccountId((current) => {
      if (current && nextAccounts.some((account) => account.accountId === current)) return current;
      return nextAccounts[0]?.accountId || '';
    });
  }, [api, clientId]);

  const loadMembers = useCallback(async (accountId: string) => {
    if (!clientId || !accountId) {
      setMembers([]);
      return;
    }
    const response = await api.get<AccountMembershipsResponse>(
      `/admin/clients/${encodeURIComponent(clientId)}/accounts/${encodeURIComponent(accountId)}/members`,
    );
    setMembers(response.members ?? []);
  }, [api, clientId]);

  useEffect(() => {
    let cancelled = false;
    if (!clientId) {
      setError('Organization routing key is required.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    Promise.all([
      api.get<ClientsResponse>('/clients', {
        query: { clientId, searchMode: 'exact', limit: 5 },
      }),
      api.get<ClientUsersResponse>(`/admin/clients/${encodeURIComponent(clientId)}/users`),
      api.get<ManagedAwsAccountsResponse>(`/admin/clients/${encodeURIComponent(clientId)}/accounts`),
    ])
      .then(([clientResponse, usersResponse, accountsResponse]) => {
        if (cancelled) return;
        const matchedClient = (clientResponse.clients ?? []).find((item) => item.clientId === clientId) ?? null;
        if (!matchedClient) throw new Error('Organization not found.');
        const nextUsers = usersResponse.users ?? [];
        setClient(matchedClient);
        setClientUsers(nextUsers);
        setAccountForm(newAccountForm(defaultAccountAdministrator(matchedClient, nextUsers)?.username || ''));
        const nextAccounts = accountsResponse.accounts ?? [];
        setAccounts(nextAccounts);
        setSelectedAccountId(nextAccounts[0]?.accountId || '');
      })
      .catch((requestError) => {
        if (!cancelled) setError(requestError instanceof Error ? requestError.message : 'Unable to load organization accounts.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, clientId]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedAccountId) {
      setMembers([]);
      return;
    }
    void loadMembers(selectedAccountId).catch((requestError) => {
      if (!cancelled) setError(requestError instanceof Error ? requestError.message : 'Unable to load account memberships.');
    });
    return () => {
      cancelled = true;
    };
  }, [loadMembers, selectedAccountId]);

  useEffect(() => {
    if (!selectedAccount) {
      setEditAccountForm(EMPTY_ACCOUNT_FORM);
      return;
    }
    setEditAccountForm(editAccountFormFromAccount(selectedAccount));
  }, [selectedAccount]);

  async function createAccount(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await api.post<ManagedAwsAccountResponse>(
        `/admin/clients/${encodeURIComponent(clientId)}/accounts`,
        {
          accountId: accountForm.accountId.trim(),
          displayName: accountForm.displayName.trim(),
          region: accountForm.region,
          cmmcLevel: accountForm.cmmcLevel,
          managementAccountId: accountForm.managementAccountId.trim(),
          logArchiveAccountId: accountForm.logArchiveAccountId.trim(),
          externalId: accountForm.externalId.trim() || undefined,
          initialAdminUsername: accountForm.initialAdminUsername,
          accountStatus: accountForm.accountStatus,
        },
      );
      setAccountForm(newAccountForm(defaultAdministrator?.username || ''));
      setAccounts((current) => {
        const next = current.some((account) => account.accountId === response.account.accountId)
          ? current.map((account) => account.accountId === response.account.accountId ? response.account : account)
          : [...current, response.account];
        return [...next].sort((left, right) => (
          Number(Boolean(right.isPrimary)) - Number(Boolean(left.isPrimary))
          || accountLabel(left).localeCompare(accountLabel(right))
        ));
      });
      setSelectedAccountId(response.account.accountId);
      setEditAccountForm(editAccountFormFromAccount(response.account));
      await loadMembers(response.account.accountId);
      setSuccess(response.message || `AWS account registered ${accountForm.accountStatus}.`);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to register AWS account.');
    } finally {
      setSaving(false);
    }
  }

  async function saveAccountMetadata(event: FormEvent) {
    event.preventDefault();
    await updateAccount(
      {
        displayName: editAccountForm.displayName.trim(),
        region: editAccountForm.region,
        cmmcLevel: editAccountForm.cmmcLevel,
        managementAccountId: editAccountForm.managementAccountId.trim(),
        logArchiveAccountId: editAccountForm.logArchiveAccountId.trim(),
        ...(editAccountForm.externalId.trim() ? { externalId: editAccountForm.externalId.trim() } : {}),
      },
      'AWS account details updated.',
    );
  }

  async function saveClientEvidenceWorkflowFeature() {
    if (!selectedAccount) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const detail = await api.get<ManagedAwsAccountResponse>(
        `/admin/clients/${encodeURIComponent(clientId)}/accounts/${encodeURIComponent(selectedAccount.accountId)}`,
      );
      const accountConfig = structuredClone(detail.account.accountConfig ?? {});
      const overrides = accountConfig.overrides && typeof accountConfig.overrides === 'object' && !Array.isArray(accountConfig.overrides)
        ? structuredClone(accountConfig.overrides as Record<string, unknown>)
        : {};
      const feature = overrides.client_evidence_workflow && typeof overrides.client_evidence_workflow === 'object' && !Array.isArray(overrides.client_evidence_workflow)
        ? structuredClone(overrides.client_evidence_workflow as Record<string, unknown>)
        : {};
      feature.enabled = editAccountForm.clientEvidenceWorkflowEnabled;
      overrides.client_evidence_workflow = feature;
      accountConfig.overrides = overrides;

      const response = await api.patch<ManagedAwsAccountResponse>(
        `/admin/clients/${encodeURIComponent(clientId)}/accounts/${encodeURIComponent(selectedAccount.accountId)}`,
        { accountConfig },
      );
      setAccounts((current) => current.map((account) => (
        account.accountId === response.account.accountId ? response.account : account
      )));
      setSelectedAccountId(response.account.accountId);
      setEditAccountForm(editAccountFormFromAccount(response.account));
      setSuccess(
        editAccountForm.clientEvidenceWorkflowEnabled
          ? 'Client-started evidence collection enabled for this AWS account.'
          : 'Client-started evidence collection disabled for this AWS account.',
      );
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to update the evidence collection feature.');
    } finally {
      setSaving(false);
    }
  }

  async function updateAccount(patch: Record<string, unknown>, message: string) {
    if (!selectedAccount) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await api.patch<ManagedAwsAccountResponse>(
        `/admin/clients/${encodeURIComponent(clientId)}/accounts/${encodeURIComponent(selectedAccount.accountId)}`,
        patch,
      );
      setAccounts((current) => current.map((account) => (
        account.accountId === response.account.accountId ? response.account : account
      )));
      setSelectedAccountId(response.account.accountId);
      setEditAccountForm(editAccountFormFromAccount(response.account));
      setSuccess(response.message || message);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to update AWS account.');
    } finally {
      setSaving(false);
    }
  }



  async function addMembership(event: FormEvent) {
    event.preventDefault();
    if (!selectedAccount) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await api.post(
        `/admin/clients/${encodeURIComponent(clientId)}/accounts/${encodeURIComponent(selectedAccount.accountId)}/members`,
        membershipForm,
      );
      setMembershipForm(EMPTY_MEMBERSHIP_FORM);
      await loadMembers(selectedAccount.accountId);
      setSuccess('Account membership saved.');
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to save account membership.');
    } finally {
      setSaving(false);
    }
  }

  async function removeMembership(member: AccountMembership) {
    if (!selectedAccount) return;
    const confirmed = window.confirm(`Remove ${memberLabel(member)} from ${accountLabel(selectedAccount)}?`);
    if (!confirmed) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await api.delete(
        `/admin/clients/${encodeURIComponent(clientId)}/accounts/${encodeURIComponent(selectedAccount.accountId)}/members/${encodeURIComponent(member.userSub)}`,
      );
      await loadMembers(selectedAccount.accountId);
      setSuccess('Account membership removed.');
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to remove account membership.');
    } finally {
      setSaving(false);
    }
  }

  async function runWorkflowForAccount(
    account: ManagedAwsAccount,
    requestedAction: string,
    workflowType: string,
    label: string,
  ) {
    const confirmed = window.confirm(`${label} for ${accountLabel(account)} (${account.accountId})?`);
    if (!confirmed) return;
    const actionKey = `${account.accountId}:${requestedAction}`;
    setRunningAction(actionKey);
    setError(null);
    setSuccess(null);
    try {
      const response = await api.post<RunClientsResponse>('/orchestration/run-client', {
        clientId,
        accountId: account.accountId,
        requestedAction,
        workflowType,
        reason: `Admin requested ${label.toLowerCase()} from the AWS account management page.`,
      });
      setSuccess(response.message || `${label} queued.`);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : `Unable to queue ${label.toLowerCase()}.`);
    } finally {
      setRunningAction(null);
    }
  }

  async function runWorkflow(requestedAction: string, workflowType: string, label: string) {
    if (!selectedAccount) return;
    await runWorkflowForAccount(selectedAccount, requestedAction, workflowType, label);
  }

  function selectManagedAccount(account: ManagedAwsAccount, scrollToDetails = false) {
    setSelectedAccountId(account.accountId);
    setEditAccountForm(editAccountFormFromAccount(account));
    setError(null);
    setSuccess(null);
    if (scrollToDetails) {
      window.requestAnimationFrame(() => {
        document.getElementById('account-details')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }

  function manageAccount(accountId: string) {
    const account = accounts.find((item) => item.accountId === accountId);
    if (account) selectManagedAccount(account, true);
  }

  if (loading) return <article className="card"><p>Loading AWS account management…</p></article>;

  return (
    <div className="accounts-management-page">
      <div className="page-heading page-heading-with-actions">
        <div>
          <h2>AWS Account Management</h2>
          <p>{client?.companyName || client?.clientName || clientId} · Organization {client?.organizationId || clientId}</p>
        </div>
        <Link className="secondary-button" to="/clients">Back to organizations</Link>
      </div>

      {error && <div className="notice notice-error">{error}</div>}
      {success && <div className="notice notice-success">{success}</div>}

      <section className="card managed-accounts-card">
        <div className="section-title-row managed-accounts-heading">
          <div>
            <h3>Organization AWS accounts</h3>
            <p className="muted-text">
              Each AWS account is an independent configuration, authorization, evidence, and workflow boundary under this organization.
            </p>
          </div>
          <div className="managed-accounts-toolbar">
            <span>{accounts.length} account{accounts.length === 1 ? '' : 's'}</span>
            <input
              aria-label="Filter managed AWS accounts"
              value={accountFilter}
              onChange={(event) => setAccountFilter(event.target.value)}
              placeholder="Filter accounts"
            />
          </div>
        </div>
        {accounts.length === 0 ? (
          <p>No managed AWS accounts are registered.</p>
        ) : (
          <div className="table-wrapper account-admin-table-wrapper">
            <table className="account-admin-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>AWS account ID</th>
                  <th>Management</th>
                  <th>Log archive</th>
                  <th>Region</th>
                  <th>CMMC</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAccounts.map((account) => (
                  <tr
                    key={account.accountId}
                    className={`account-admin-row ${account.accountId === selectedAccountId ? 'account-admin-row-selected' : ''}`.trim()}
                    aria-selected={account.accountId === selectedAccountId}
                    tabIndex={0}
                    onClick={() => selectManagedAccount(account)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        selectManagedAccount(account);
                      }
                    }}
                  >
                    <td><strong>{accountLabel(account)}</strong>{account.isPrimary ? <span className="account-primary-label">Primary</span> : null}</td>
                    <td className="mono-cell">{account.accountId}</td>
                    <td className="mono-cell">{account.managementAccountId || '—'}</td>
                    <td className="mono-cell">{account.logArchiveAccountId || '—'}</td>
                    <td>{account.region || '—'}</td>
                    <td>{account.cmmcLevel || '—'}</td>
                    <td><span className={statusPillClassName(account.accountStatus)}>{account.accountStatus}</span></td>
                    <td>
                      <div className="table-action-stack account-registry-actions" onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
                        <Link
                          className="primary-button table-action-button"
                          to={`/clients?configureClientId=${encodeURIComponent(clientId)}&accountId=${encodeURIComponent(account.accountId)}`}
                        >
                          Configure
                        </Link>
                        <button className="secondary-button table-action-button" type="button" onClick={() => manageAccount(account.accountId)}>Details</button>
                        <button className="secondary-button table-action-button" type="button" disabled={Boolean(runningAction) || account.accountStatus !== 'ACTIVE'} onClick={() => void runWorkflowForAccount(account, 'RUN_EVIDENCE_WORKFLOW', 'technical-baseline', 'Full workflow')}>{runningAction === `${account.accountId}:RUN_EVIDENCE_WORKFLOW` ? 'Queueing…' : 'Full'}</button>
                        <button className="secondary-button table-action-button" type="button" disabled={Boolean(runningAction) || account.accountStatus !== 'ACTIVE'} onClick={() => void runWorkflowForAccount(account, 'RUN_CDK_DEPLOYMENT', 'software-update', 'Software deployment')}>{runningAction === `${account.accountId}:RUN_CDK_DEPLOYMENT` ? 'Queueing…' : 'Deploy'}</button>
                        <button className="success-button table-action-button" type="button" disabled={Boolean(runningAction) || account.accountStatus !== 'ACTIVE'} onClick={() => void runWorkflowForAccount(account, 'RUN_EVIDENCE_PACKAGE', 'evidence-package', 'Evidence collection')}>{runningAction === `${account.accountId}:RUN_EVIDENCE_PACKAGE` ? 'Queueing…' : 'Evidence'}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredAccounts.length === 0 && <p className="muted-text account-filter-empty">No accounts match this filter.</p>}
          </div>
        )}
      </section>

      <details className="card account-register-card">
        <summary>{accounts.length === 0 ? 'Register first AWS account' : 'Register another AWS account'}</summary>
        <p className="muted-text">
          The account receives its own configuration and account administrator when registered. The first account becomes primary automatically. Choose whether the new account should be active immediately or remain disabled until onboarding is ready.
        </p>
        <form className="form-grid account-admin-form" onSubmit={createAccount}>
          <label>AWS account ID<input required pattern="\d{12}" value={accountForm.accountId} onChange={(event) => setAccountForm((current) => ({ ...current, accountId: event.target.value }))} placeholder="123456789012" /></label>
          <label>Display name<input required value={accountForm.displayName} onChange={(event) => setAccountForm((current) => ({ ...current, displayName: event.target.value }))} placeholder="Program or customer environment" /></label>
          <label>Management account ID<input required pattern="\d{12}" value={accountForm.managementAccountId} onChange={(event) => setAccountForm((current) => ({ ...current, managementAccountId: event.target.value }))} placeholder="123456789012" /></label>
          <label>Log archive account ID<input required pattern="\d{12}" value={accountForm.logArchiveAccountId} onChange={(event) => setAccountForm((current) => ({ ...current, logArchiveAccountId: event.target.value }))} placeholder="123456789012" /></label>
          <label>Region<select value={accountForm.region} onChange={(event) => setAccountForm((current) => ({ ...current, region: event.target.value }))}><option value="us-east-1">us-east-1</option><option value="us-east-2">us-east-2</option><option value="us-west-2">us-west-2</option></select></label>
          <label>External ID<input type="password" minLength={32} maxLength={256} value={accountForm.externalId} onChange={(event) => setAccountForm((current) => ({ ...current, externalId: event.target.value }))} placeholder="Paste the account-specific External ID" autoComplete="new-password" /></label>
          <label>CMMC level<select value={accountForm.cmmcLevel} onChange={(event) => setAccountForm((current) => ({ ...current, cmmcLevel: event.target.value }))}><option value="L1">L1</option><option value="L2">L2</option></select></label>
          <label>
            Default account administrator
            <select required value={accountForm.initialAdminUsername} onChange={(event) => setAccountForm((current) => ({ ...current, initialAdminUsername: event.target.value }))}>
              <option value="">Select an organization administrator</option>
              {eligibleAccountAdministrators.map((user) => (
                <option key={user.username} value={user.username}>{user.email || user.username}</option>
              ))}
            </select>
            <span className="field-help">The organization invitation user is selected by default. Only CLIENT_ADMIN users can administer an AWS account.</span>
          </label>
          <label>
            Initial account status
            <select value={accountForm.accountStatus} onChange={(event) => setAccountForm((current) => ({ ...current, accountStatus: event.target.value as AccountFormState['accountStatus'] }))}>
              <option value="DISABLED">Disabled</option>
              <option value="ACTIVE">Active</option>
            </select>
            <span className="field-help">Active accounts are immediately available to the selected administrator and can run workflows.</span>
          </label>
          <div className="form-actions"><button className="primary-button" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Register account'}</button></div>
        </form>
      </details>

      {selectedAccount && (
        <>
          <section id="account-details" className="card account-admin-detail">
            <div className="section-title-row">
              <div>
                <h3>Edit AWS account</h3>
                <p>{accountLabel(selectedAccount)}</p>
                <p className="mono-cell">{selectedAccount.accountId}</p>
              </div>
              <span className={statusPillClassName(selectedAccount.accountStatus)}>{selectedAccount.accountStatus}</span>
            </div>
            <div className="detail-grid">
              <section className="detail-section-wide">
                <h4>Account configuration</h4>
                <p className="muted-text">Update this account’s independent identity and AWS organization boundaries, then select Save account details.</p>
                <form className="account-edit-form" onSubmit={saveAccountMetadata}>
                  <label>Display name<input required value={editAccountForm.displayName} onChange={(event) => setEditAccountForm((current) => ({ ...current, displayName: event.target.value }))} /></label>
                  <label>Management account ID<input required pattern="\d{12}" value={editAccountForm.managementAccountId} onChange={(event) => setEditAccountForm((current) => ({ ...current, managementAccountId: event.target.value }))} /></label>
                  <label>Log archive account ID<input required pattern="\d{12}" value={editAccountForm.logArchiveAccountId} onChange={(event) => setEditAccountForm((current) => ({ ...current, logArchiveAccountId: event.target.value }))} /></label>
                  <label>Region<select value={editAccountForm.region} onChange={(event) => setEditAccountForm((current) => ({ ...current, region: event.target.value }))}><option value="us-east-1">us-east-1</option><option value="us-east-2">us-east-2</option><option value="us-west-2">us-west-2</option></select></label>
                  <label>External ID<input type="password" minLength={32} maxLength={256} value={editAccountForm.externalId} onChange={(event) => setEditAccountForm((current) => ({ ...current, externalId: event.target.value }))} placeholder={selectedAccount.externalIdStatus === 'CONFIGURED' ? 'Enter only to replace the configured value' : 'Paste the account-specific External ID'} autoComplete="new-password" /></label>
                  <div className="detail-row"><span>External ID</span><strong>{selectedAccount.externalIdStatus === 'CONFIGURED' ? `Configured · version ${selectedAccount.externalIdVersion || 1}` : 'Not configured'}</strong></div>
                  <label>CMMC level<select value={editAccountForm.cmmcLevel} onChange={(event) => setEditAccountForm((current) => ({ ...current, cmmcLevel: event.target.value }))}><option value="L1">L1</option><option value="L2">L2</option></select></label>
                  <div className="detail-row"><span>Primary</span><strong>{selectedAccount.isPrimary ? 'Yes' : 'No'}</strong></div>
                  <div className="detail-row"><span>Updated</span><strong>{formatLocalDateTime(selectedAccount.updatedAt)}</strong></div>
                  <div className="account-edit-actions">
                    <button className="primary-button" type="submit" disabled={saving}>Save account details</button>
                  </div>
                </form>
              </section>
              <section><h4>Account status</h4><p className="muted-text">An active account must have an active account administrator. Disabling an account blocks access and new workflows without deleting evidence.</p><button className={selectedAccount.accountStatus === 'ACTIVE' ? 'danger-button' : 'success-button'} type="button" disabled={saving} onClick={() => void updateAccount({ accountStatus: selectedAccount.accountStatus === 'ACTIVE' ? 'DISABLED' : 'ACTIVE', ...(selectedAccount.accountStatus === 'ACTIVE' || !defaultAdministrator ? {} : { accountAdminUsername: defaultAdministrator.username }) }, 'AWS account status updated.')}>{selectedAccount.accountStatus === 'ACTIVE' ? 'Disable account' : 'Enable account'}</button></section>
              <section>
                <h4>Paid features</h4>
                <p className="muted-text">Enable account-level client capabilities. Evidence collection requests still require approval or decline before collection begins.</p>
                <label>
                  Client-started evidence collection
                  <select
                    value={editAccountForm.clientEvidenceWorkflowEnabled ? 'ENABLED' : 'DISABLED'}
                    onChange={(event) => setEditAccountForm((current) => ({
                      ...current,
                      clientEvidenceWorkflowEnabled: event.target.value === 'ENABLED',
                    }))}
                  >
                    <option value="DISABLED">Disabled</option>
                    <option value="ENABLED">Enabled</option>
                  </select>
                </label>
                <button className="primary-button" type="button" disabled={saving} onClick={() => void saveClientEvidenceWorkflowFeature()}>
                  Save feature setting
                </button>
              </section>
              <section><h4>Workflows</h4><p className="muted-text">Workflow requests are bound to this exact AWS account.</p><div className="button-row"><button className="primary-button" type="button" disabled={Boolean(runningAction) || selectedAccount.accountStatus !== 'ACTIVE'} onClick={() => void runWorkflow('RUN_EVIDENCE_WORKFLOW', 'technical-baseline', 'Full workflow')}>{runningAction === `${selectedAccount.accountId}:RUN_EVIDENCE_WORKFLOW` ? 'Queueing…' : 'Full workflow'}</button><button className="secondary-button" type="button" disabled={Boolean(runningAction) || selectedAccount.accountStatus !== 'ACTIVE'} onClick={() => void runWorkflow('RUN_CDK_DEPLOYMENT', 'software-update', 'Software deployment')}>{runningAction === `${selectedAccount.accountId}:RUN_CDK_DEPLOYMENT` ? 'Queueing…' : 'Deploy software'}</button><button className="success-button" type="button" disabled={Boolean(runningAction) || selectedAccount.accountStatus !== 'ACTIVE'} onClick={() => void runWorkflow('RUN_EVIDENCE_PACKAGE', 'evidence-package', 'Evidence collection')}>{runningAction === `${selectedAccount.accountId}:RUN_EVIDENCE_PACKAGE` ? 'Queueing…' : 'Collect evidence'}</button></div></section>
            </div>
          </section>

          <section className="card">
            <div className="section-title-row"><div><h3>Account memberships</h3><p className="muted-text">Membership controls which organization users can select and access this account.</p></div><span>{members.length} member{members.length === 1 ? '' : 's'}</span></div>
            <form className="account-membership-form" onSubmit={addMembership}>
              <label>Organization user<select required value={membershipForm.username} onChange={(event) => setMembershipForm((current) => ({ ...current, username: event.target.value }))}><option value="">Select an organization portal user</option>{clientUsers.map((user) => <option key={user.username} value={user.username}>{user.email || user.username} · {user.groupName || 'Client user'}</option>)}</select></label>
              <label>Account role<select value={membershipForm.accountRole} onChange={(event) => setMembershipForm((current) => ({ ...current, accountRole: event.target.value as MembershipFormState['accountRole'] }))}><option value="ACCOUNT_READONLY">Read-only</option><option value="ACCOUNT_ADMIN">Account administrator</option></select></label>
              <button className="primary-button" type="submit" disabled={saving}>Assign user</button>
            </form>
            <div className="table-wrapper account-membership-table-wrapper">
              <table className="account-membership-table"><thead><tr><th>User</th><th>Role</th><th>Status</th><th>Updated</th><th>Action</th></tr></thead><tbody>{members.map((member) => <tr key={member.userSub}><td>{memberLabel(member)}</td><td>{member.accountRole === 'ACCOUNT_ADMIN' ? 'Account administrator' : 'Read-only'}</td><td>{member.membershipStatus || 'ACTIVE'}</td><td>{formatLocalDateTime(member.updatedAt || member.createdAt)}</td><td><button className="danger-button table-action-button" type="button" disabled={saving} onClick={() => void removeMembership(member)}>Remove</button></td></tr>)}</tbody></table>
              {members.length === 0 && <p className="muted-text">No users are assigned to this account.</p>}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
